// Points at the check-in app's public booking-config endpoint.
var CONFIG_URL = 'https://checkin.rockinr.com/api/booking-config';

/**
 * Shared booking-availability client.
 *
 * Reads the manager-controlled closure/product config the check-in app serves
 * and exposes it to the theme's copies of the schedule logic
 * (sections/page-booking.liquid, sections/page-find-your-float.liquid).
 *
 * TWO DIRECTIONS, ONE PRECEDENCE. Closures are subtractive and openings are
 * additive, and a closure ALWAYS beats an overlapping opening. That ordering is
 * not enforced in this file — each lookup answers only its own question — it is
 * enforced by the caller, which must consult getClosure/isDayClosed BEFORE
 * openingTimesFor. See getEffectiveSchedule + getDateAvailability in
 * sections/page-booking.liquid for the reference implementation.
 *
 * FAILS TO THE BAKED-IN SCHEDULE, both ways. Every lookup here degrades to
 * "no closure, no opening, nothing hidden", so a dead endpoint leaves the
 * theme's own schedule exactly as it is: it can neither empty the calendar nor
 * invent a departure. Note the asymmetry in what that costs — a dropped closure
 * over-sells a closed day, a dropped opening under-sells an open one. The
 * second is lost revenue; the first puts customers at a closed launch. Neither
 * silently opens something.
 *
 * Payload shape (verified against the live endpoint and
 * rockingr-checkin-app/lib/booking-config.ts):
 *   {
 *     locations:    [{ slug, label, opsName, weekdayHandle, weekendHandle, sortOrder, active, closedReason }],
 *     closures:     [{ id, locationSlug, startsOn, endsOn, times, reason }],
 *     openings:     [{ id, locationSlug, startsOn, endsOn, daysOfWeek, times, reason }],
 *     schedules:    [{ id, locationSlug, startsOn, endsOn, daysOfWeek, times, priority, offDayReason }],
 *     productRules: [{ locationSlug, productId, visible }],
 *     generatedAt:  ISO string
 *   }
 *
 * Three strings here are customer-facing and rendered by the theme: a
 * closure's reason, a window's offDayReason, and a location's closedReason.
 * Window names are admin-only and never leave the check-in app.
 *
 * `schedules` (migration 036) is the season baseline that page-booking.liquid
 * still hardcodes in getLocationSchedule(). Only scheduleFor() reads it, and
 * scheduleFor() is the one lookup here that FAILS CLOSED — see its comment.
 *
 * An opening's locationSlug is NEVER null — the admin API rejects a
 * location-wide opening and the server reader drops any that reaches it — so
 * there is deliberately no all-locations branch on the opening side, unlike
 * closures. daysOfWeek is null (every day in the range) or an array of
 * integers, 0 = Sunday through 6 = Saturday. times is non-empty and holds full
 * slot labels, e.g. '9:00 AM'.
 *
 * Usage:
 *   RnrAvailability.ready.then(function() {
 *     if (RnrAvailability.isDayClosed('gruene', dateObj)) { ... }
 *     var extra = RnrAvailability.openingTimesFor('gruene', dateObj);
 *   });
 */
(function() {
  'use strict';

  var TAG = '[RnrAvailability]';

  // A hanging request must not stall the calendar. On timeout we degrade to
  // the no-config path, same as any other failure.
  var FETCH_TIMEOUT_MS = 4000;

  var api = {
    ready: null,
    loaded: false,
    config: null,
    getClosure: getClosure,
    isDayClosed: isDayClosed,
    openingTimesFor: openingTimesFor,
    hiddenProductIds: hiddenProductIds,
    scheduleFor: scheduleFor,
    offersTime: offersTime,
    clockMinutes: clockMinutes
  };

  // ── Date handling ──────────────────────────────────────────────────────────
  // Callers pass Dates built as new Date(year, month, day) — local midnight.
  // toISOString() would shift those to UTC and land on the previous day for any
  // Central-time evening, so this formats from the local getters instead. Same
  // pattern as isBlockedDate() in sections/page-booking.liquid.
  function toLocalYMD(d) {
    if (!d || typeof d.getFullYear !== 'function') return null;
    var y = d.getFullYear();
    if (isNaN(y)) return null;
    return y + '-' +
      String(d.getMonth() + 1).padStart(2, '0') + '-' +
      String(d.getDate()).padStart(2, '0');
  }

  function closures() {
    if (!api.config || !Array.isArray(api.config.closures)) return [];
    return api.config.closures;
  }

  // null locationSlug means the closure covers every location.
  function appliesToLocation(closure, slug) {
    return closure.locationSlug === null ||
           closure.locationSlug === undefined ||
           closure.locationSlug === slug;
  }

  // Zero-padded ISO dates compare correctly as plain strings.
  function coversDate(closure, ymd) {
    return typeof closure.startsOn === 'string' &&
           typeof closure.endsOn === 'string' &&
           ymd >= closure.startsOn &&
           ymd <= closure.endsOn;
  }

  // times null/empty = the whole day is closed. Otherwise only the listed
  // slots are blocked; entries are exact theme slot labels, e.g. '3:45 PM'.
  function isWholeDay(closure) {
    return !Array.isArray(closure.times) || closure.times.length === 0;
  }

  function blocksTime(closure, time) {
    return Array.isArray(closure.times) && closure.times.indexOf(time) !== -1;
  }

  // ── Openings ───────────────────────────────────────────────────────────────

  function openings() {
    if (!api.config || !Array.isArray(api.config.openings)) return [];
    return api.config.openings;
  }

  // Minutes since midnight for a slot label. Only used for ORDERING the merged
  // list — never for matching, which is always on the exact label string.
  //
  // 12:00 PM is noon (720) and 12:00 AM is midnight (0): the one hour where
  // "add 12 for PM" is wrong in both directions. Returns null rather than 0 for
  // an unreadable label, so the sort can push it to the end instead of
  // pretending it departs at midnight.
  function parseClockMinutes(label) {
    if (typeof label !== 'string') return null;
    var m = label.trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp])\.?[Mm]\.?$/);
    if (!m) return null;
    var hour = parseInt(m[1], 10);
    var minute = parseInt(m[2], 10);
    if (hour < 1 || hour > 12 || minute > 59) return null;
    var isPM = m[3].toLowerCase() === 'p';
    if (hour === 12) hour = 0;
    return (isPM ? hour + 12 : hour) * 60 + minute;
  }

  // An opening's location must match EXACTLY. There is no null/all-locations
  // branch here on purpose — see the note in the file header.
  function openingAppliesToLocation(opening, slug) {
    return typeof opening.locationSlug === 'string' &&
           opening.locationSlug === slug;
  }

  // dow is the weekday of the caller's local Date (0 = Sunday), taken from
  // getDay() rather than re-derived from the YMD string: building a Date from
  // 'YYYY-MM-DD' parses as UTC midnight, which is the previous day in Central
  // time and therefore the wrong weekday.
  function openingCoversDay(opening, dow) {
    if (opening.daysOfWeek === null || opening.daysOfWeek === undefined) return true;
    if (!Array.isArray(opening.daysOfWeek)) return false;
    return opening.daysOfWeek.indexOf(dow) !== -1;
  }

  /**
   * Departure times a manager has added at this location on this date.
   *
   * Does NOT consider closures — a caller that ignores getClosure first will
   * happily open a closed day. The precedence lives with the caller.
   *
   * @param   {string} slug
   * @param   {Date}   dateObj
   * @returns {string[]} full slot labels, chronological, deduped; [] when none
   */
  function openingTimesFor(slug, dateObj) {
    var ymd = toLocalYMD(dateObj);
    if (!ymd) return [];
    if (!dateObj || typeof dateObj.getDay !== 'function') return [];

    var dow = dateObj.getDay();
    var list = openings();
    var out = [];

    for (var i = 0; i < list.length; i++) {
      var o = list[i];
      if (!o) continue;
      if (!openingAppliesToLocation(o, slug)) continue;
      // Same string comparison the closure path uses — zero-padded ISO dates
      // compare correctly as plain strings.
      if (!coversDate(o, ymd)) continue;
      if (!openingCoversDay(o, dow)) continue;
      if (!Array.isArray(o.times)) continue;

      // Union across every matching opening: two openings can legitimately
      // cover the same date with different departures.
      for (var j = 0; j < o.times.length; j++) {
        var t = o.times[j];
        if (typeof t !== 'string' || !t) continue;
        if (out.indexOf(t) === -1) out.push(t);
      }
    }

    // Chronological, not lexicographic — '10:00 AM' sorts before '9:00 AM' as a
    // string, which would render the time row out of order. Unparseable labels
    // sort to the end rather than to midnight.
    out.sort(function(a, b) {
      var ma = parseClockMinutes(a);
      var mb = parseClockMinutes(b);
      if (ma === null && mb === null) return 0;
      if (ma === null) return 1;
      if (mb === null) return -1;
      return ma - mb;
    });

    return out;
  }

  // ── Public lookups ─────────────────────────────────────────────────────────

  /**
   * @param   {string} slug
   * @param   {Date}   dateObj
   * @param   {string} [time]  exact slot label, e.g. '3:45 PM'
   * @returns {object|null}    the matching closure, or null
   */
  function getClosure(slug, dateObj, time) {
    var ymd = toLocalYMD(dateObj);
    if (!ymd) return null;

    var list = closures();
    for (var i = 0; i < list.length; i++) {
      var c = list[i];
      if (!c) continue;
      if (!appliesToLocation(c, slug)) continue;
      if (!coversDate(c, ymd)) continue;

      if (time === undefined || time === null) {
        // No slot asked about — only a whole-day closure answers.
        if (isWholeDay(c)) return c;
        continue;
      }
      if (isWholeDay(c) || blocksTime(c, time)) return c;
    }
    return null;
  }

  /**
   * @returns {object|null} a whole-day closure for this date, or null
   */
  function isDayClosed(slug, dateObj) {
    return getClosure(slug, dateObj);
  }

  /**
   * Product ids the manager has hidden at this location. The endpoint only
   * ships explicit hides (visible === false); a product with no row is visible.
   *
   * Ids are normalized to strings so callers can compare against
   * Liquid-rendered ids without type surprises.
   *
   * @returns {string[]}
   */
  function hiddenProductIds(slug) {
    if (!api.config || !Array.isArray(api.config.productRules)) return [];

    var out = [];
    for (var i = 0; i < api.config.productRules.length; i++) {
      var r = api.config.productRules[i];
      if (!r) continue;
      if (r.locationSlug !== slug) continue;
      if (r.visible !== false) continue;
      if (r.productId === null || r.productId === undefined) continue;
      out.push(String(r.productId));
    }
    return out;
  }

  // ── Effective schedule ─────────────────────────────────────────────────────
  // A port of evaluateSchedule() in rockingr-checkin-app/lib/booking-config.ts,
  // which is what POST /api/reschedule/{token} validates against. Keep the two
  // in step: a date this answers open and the server answers closed is a
  // customer clicking a day only to be refused.

  var UNAVAILABLE = 'Reservations are not available on this date.';
  var NOT_LOADED = 'We couldn’t load availability right now. Please refresh the page, or call us at 830-629-9999.';

  // The server's parser, which is looser than parseClockMinutes above: it also
  // takes '11am' and 24-hour '14:00'. Order times come from Shopify and need not
  // match the slot labels' shape, so anything matching an order's time against
  // a schedule goes through this one.
  function clockMinutes(raw) {
    if (!raw) return null;
    var s = String(raw).trim();
    var m = s.match(/^(\d{1,2})(?::(\d{2}))?\s*([AaPp])\.?[Mm]\.?$/);
    if (m) {
      var hour = parseInt(m[1], 10);
      var minute = m[2] === undefined ? 0 : parseInt(m[2], 10);
      if (hour < 1 || hour > 12 || minute > 59) return null;
      var isPM = m[3].toLowerCase() === 'p';
      if (hour === 12) hour = 0;
      return (isPM ? hour + 12 : hour) * 60 + minute;
    }
    var h24 = s.match(/^(\d{1,2}):(\d{2})$/);
    if (h24) {
      var h = parseInt(h24[1], 10);
      var mi = parseInt(h24[2], 10);
      if (h > 23 || mi > 59) return null;
      return h * 60 + mi;
    }
    return null;
  }

  function sortByClock(times) {
    return times.slice().sort(function(a, b) {
      var ma = clockMinutes(a);
      var mb = clockMinutes(b);
      if (ma === null && mb === null) return 0;
      if (ma === null) return 1;
      if (mb === null) return -1;
      return ma - mb;
    });
  }

  // null on either side = unbounded. Schedule windows use that; closures and
  // openings always carry both dates.
  function inWindow(ymd, startsOn, endsOn) {
    if (typeof startsOn === 'string' && ymd < startsOn) return false;
    if (typeof endsOn === 'string' && ymd > endsOn) return false;
    return true;
  }

  function closedReasonFor(slug) {
    var list = (api.config && Array.isArray(api.config.locations)) ? api.config.locations : [];
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].slug === slug) return list[i].closedReason || null;
    }
    return null;
  }

  /**
   * Is this location open on this date, and which departures run?
   *
   * Precedence, as evaluateSchedule applies it:
   *   1. whole-day closure       -> closed, the closure's reason
   *   2. schedule window         -> highest priority wins; open with its times,
   *                                 or closed with offDayReason on an excluded
   *                                 weekday; no window -> location's closedReason
   *   3. opening                 -> open, its times UNIONED with step 2's
   *   4. partial closure         -> subtract its departures from whatever 2-3
   *                                 left; closed with its reason if none remain
   *
   * `source` names the layer that decided: 'not-loaded', 'invalid',
   * 'closure', 'window', 'off-day', 'no-window', 'opening' (an opening covers
   * the date) or 'partial-closure' (one emptied the day). A
   * partial closure that only trims leaves the earlier layer's source.
   *
   * FAILS CLOSED, unlike every other lookup in this file. The others are deltas
   * against a schedule the theme already has, so "no config" means "no delta".
   * This one IS the schedule: with no config, or a config without `schedules`,
   * there is nothing to say a day is open, and it backs a write path where
   * refusing is the safe direction.
   *
   * @param   {string} slug
   * @param   {Date}   dateObj  local-midnight Date, new Date(y, m, d)
   * @returns {{ open: boolean, times: string[], reason: string|null, source: string }}
   */
  function scheduleFor(slug, dateObj) {
    if (!api.loaded || !api.config || !Array.isArray(api.config.schedules)) {
      return { open: false, times: [], reason: NOT_LOADED, source: 'not-loaded' };
    }
    var ymd = toLocalYMD(dateObj);
    if (!ymd || !slug) return { open: false, times: [], reason: UNAVAILABLE, source: 'invalid' };
    var dow = dateObj.getDay();

    // 1. Whole-day closure.
    var cl = closures();
    for (var i = 0; i < cl.length; i++) {
      var c = cl[i];
      if (!c || !appliesToLocation(c, slug) || !isWholeDay(c)) continue;
      if (coversDate(c, ymd)) return { open: false, times: [], reason: c.reason || null, source: 'closure' };
    }

    // 2. Highest-priority window covering the date; ties go to the lower id.
    var matching = api.config.schedules.filter(function(w) {
      return w && w.locationSlug === slug && Array.isArray(w.times) &&
             inWindow(ymd, w.startsOn, w.endsOn);
    }).sort(function(a, b) {
      return (Number(b.priority) || 0) - (Number(a.priority) || 0) || a.id - b.id;
    });

    var closedReason = closedReasonFor(slug);
    var result;
    if (!matching.length) {
      result = { open: false, times: [], reason: closedReason || UNAVAILABLE, source: 'no-window' };
    } else {
      var w = matching[0];
      var dayAllowed = w.daysOfWeek === null || w.daysOfWeek === undefined ||
                       (Array.isArray(w.daysOfWeek) && w.daysOfWeek.indexOf(dow) !== -1);
      result = dayAllowed
        ? { open: true, times: sortByClock(w.times), reason: null, source: 'window' }
        : { open: false, times: [], reason: w.offDayReason || closedReason || UNAVAILABLE, source: 'off-day' };
    }

    // 3. Openings, unioned onto the window's times (or onto nothing).
    var extra = openingTimesFor(slug, dateObj);
    if (extra.length) {
      var merged = result.open ? result.times.slice() : [];
      for (var j = 0; j < extra.length; j++) {
        if (merged.indexOf(extra[j]) === -1) merged.push(extra[j]);
      }
      result = { open: true, times: sortByClock(merged), reason: null, source: 'opening' };
    }

    // 4. Partial closures, last, so they beat an opening on the same slot.
    // Matched by label or by clock value, as the server does.
    if (!result.open) return result;
    var times = result.times;
    var emptiedBy = null;
    for (var k = 0; k < cl.length; k++) {
      var pc = cl[k];
      if (!pc || isWholeDay(pc) || !appliesToLocation(pc, slug) || !coversDate(pc, ymd)) continue;
      var closedMinutes = pc.times.map(clockMinutes).filter(function(x) { return x !== null; });
      var before = times.length;
      times = times.filter(function(t) {
        if (pc.times.indexOf(t) !== -1) return false;
        var mm = clockMinutes(t);
        return mm === null || closedMinutes.indexOf(mm) === -1;
      });
      if (before > 0 && times.length === 0) emptiedBy = pc;
    }
    if (!times.length) {
      return {
        open: false,
        times: [],
        reason: (emptiedBy && emptiedBy.reason) || 'No departures are available on this date.',
        source: 'partial-closure'
      };
    }
    return { open: true, times: times, reason: null, source: result.source };
  }

  /**
   * Whether a schedule from scheduleFor() runs this departure. Compared by
   * clock value, as the server's time_unavailable check is.
   */
  function offersTime(schedule, time) {
    var want = clockMinutes(time);
    if (want === null || !schedule || !schedule.open || !Array.isArray(schedule.times)) return false;
    for (var i = 0; i < schedule.times.length; i++) {
      if (clockMinutes(schedule.times[i]) === want) return true;
    }
    return false;
  }

  // ── Load ───────────────────────────────────────────────────────────────────
  // Resolves either way and never rejects: a config failure is a normal
  // outcome that leaves the theme on its baked-in schedule.
  //
  // Deliberately no sessionStorage/localStorage. The endpoint is edge-cached
  // for 60s, which is the right staleness window; a client-side cache would
  // make a manager's closure take longer to reach customers.
  function load() {
    if (typeof window.fetch !== 'function') {
      console.warn(TAG + ' fetch unavailable; continuing without remote config');
      return Promise.resolve();
    }

    return new Promise(function(resolve) {
      var settled = false;
      function finish() {
        if (settled) return;
        settled = true;
        resolve();
      }

      // Timeout is enforced here rather than only via AbortController so the
      // promise resolves on schedule even where abort isn't supported.
      var timer = setTimeout(function() {
        console.warn(TAG + ' config request timed out after ' + FETCH_TIMEOUT_MS + 'ms');
        if (controller) { try { controller.abort(); } catch (e) {} }
        finish();
      }, FETCH_TIMEOUT_MS);

      var controller = null;
      var opts = { credentials: 'omit' };
      if (typeof window.AbortController === 'function') {
        controller = new window.AbortController();
        opts.signal = controller.signal;
      }

      var req;
      try {
        req = window.fetch(CONFIG_URL, opts);
      } catch (err) {
        clearTimeout(timer);
        console.warn(TAG + ' config request threw:', err);
        finish();
        return;
      }

      req.then(function(res) {
        if (!res.ok) {
          throw new Error('HTTP ' + res.status);
        }
        return res.json();
      }).then(function(json) {
        if (!json || typeof json !== 'object') {
          throw new Error('payload was not an object');
        }
        api.config = json;
        api.loaded = true;
      }).catch(function(err) {
        // Network error, non-200, or malformed JSON — all the same outcome.
        api.config = null;
        api.loaded = false;
        console.warn(TAG + ' could not load booking config:', err && err.message ? err.message : err);
      }).then(function() {
        clearTimeout(timer);
        finish();
      });
    });
  }

  api.ready = load();

  window.RnrAvailability = api;
})();
